import GLightbox from "glightbox";
import initializeTooltip, { setOverflowTitles } from "./utils/tooltips";
import { initEmblaCarousel, initEmblaCarouselGallery } from "./utils/emblaCarousel";
import { initFormset } from "./utils/formsetHelpers";
import initPageToc from "./pageToc";
import initShareButton from "./shareButton";
import initImageFieldToggle from "./imageFieldToggle";

function initGLightbox(target) {
  const fragmentSelector = `[data-fragment-id="${target.dataset.fragmentId}"]`;

  // Get all unique gallery IDs within this fragment
  const glightboxElements = target.querySelectorAll(".glightbox[data-gallery]");
  const galleryIds = [
    ...new Set([...glightboxElements].map((el) => el.dataset.gallery)),
  ];

  // Initialize a separate GLightbox instance for each gallery group
  galleryIds.forEach((galleryId) => {
    GLightbox({
      selector: `${fragmentSelector} .glightbox[data-gallery="${galleryId}"]`,
      width: "90vw",
      height: "90vh",
    });
  });

  // Also handle any glightbox elements without data-gallery (fallback)
  const ungroupedElements = target.querySelectorAll(
    ".glightbox:not([data-gallery])",
  );
  if (ungroupedElements.length > 0) {
    GLightbox({
      selector: `${fragmentSelector} .glightbox:not([data-gallery])`,
      width: "90vw",
      height: "90vh",
    });
  }
}

function initFileDragHighlight(target) {
  target.querySelectorAll("input[type=file]").forEach(function (input) {
    function add() {
      input.classList.add("is-dragover");
    }
    function remove() {
      input.classList.remove("is-dragover");
    }
    ["dragenter", "dragover"].forEach(function (evt) {
      input.addEventListener(evt, add);
    });
    ["dragleave", "drop", "dragend"].forEach(function (evt) {
      input.addEventListener(evt, remove);
    });
  });
}

let overflowTitlesResizeTimer;
window.addEventListener("resize", function () {
  clearTimeout(overflowTitlesResizeTimer);
  overflowTitlesResizeTimer = setTimeout(function () {
    setOverflowTitles(document);
  }, 150);
});

function initTabLinks(target) {
  target.querySelectorAll("[data-update-field]").forEach(function (link) {
    link.addEventListener("click", function (e) {
      e.preventDefault();
      var field = document.getElementById(link.dataset.updateField);
      if (!field) return;
      field.value = link.dataset.value || "";
      link.closest("nav").querySelectorAll("[aria-current]").forEach(function (a) {
        a.removeAttribute("aria-current");
      });
      link.setAttribute("aria-current", "page");
      field.dispatchEvent(new Event("change", { bubbles: true }));
    });
  });
}

export default function initWidgets(target) {
  if (!("fragmentId" in target.dataset)) {
    target.dataset.fragmentId = crypto.randomUUID();
  }

  initGLightbox(target);

  // Select2 pulls in jQuery, so it is code-split into its own async chunk and
  // only loaded when a Select2 widget is actually on the page — keeping jQuery
  // out of the main bundle. initOtherFieldsToggle lives in the same chunk: it
  // reacts to Select2's (jQuery-dispatched) change events and must run after
  // initialisation.
  if (target.querySelector("select.select2")) {
    Promise.all([
      import("./utils/select2Helpers"),
      import("./utils/formHelpers"),
    ]).then(function ([{ initializeSelect2 }, { initOtherFieldsToggle }]) {
      target.querySelectorAll("select.select2").forEach(function (el) {
        initializeSelect2(el);
      });
      initOtherFieldsToggle(target);
    });
  }

  initFileDragHighlight(target);

  target.querySelectorAll(".formset").forEach(function (el) {
    initFormset(el);
  });

  target.querySelectorAll("[data-toggle=tooltip], [data-tooltip-title]").forEach(function (el) {
    initializeTooltip(el);
  });

  initTabLinks(target);

  setOverflowTitles(target);

  target.querySelectorAll("[data-embla]").forEach(initEmblaCarousel);
  target.querySelectorAll("[data-embla-gallery]").forEach(initEmblaCarouselGallery);

  initPageToc(target);
  initShareButton(target);
  initImageFieldToggle(target);

  var calendarEl = target.querySelector("[data-calendar]");
  if (calendarEl) {
    import("./calendar/index.js").then(function ({ initCalendar }) {
      initCalendar(calendarEl);
    });
  }
}
